import FormModal from "@/components/modal/FormModal";
import ControlledInputField from "@/components/ui/form/ControlledInputField";
import { useAudiolab } from "@/components/providers/AudiolabProvider/hooks";
import { SITE_URL } from "@/lib/config/env-var.config";
import {
  useCreatorsControllerCreateCreator,
  creatorsControllerDetailCreator,
  getCreatorsControllerDetailCreatorQueryKey,
} from "@/lib/lutely-api/gen/creators/creators";
import { getAuthControllerGetCurrentUserQueryKey } from "@/lib/lutely-api/gen/auth/auth";
import { typographyTokens } from "@/lib/tokens";
import { Flex, Text } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "react-toastify";

type AddArtistModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type AddArtistForm = {
  stageName: string;
  slug: string;
};

export default function AddArtistModal({
  open,
  onOpenChange,
}: AddArtistModalProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setSelectedCreator } = useAudiolab();
  const createCreator = useCreatorsControllerCreateCreator();

  const handleSubmit = async (values: AddArtistForm) => {
    try {
      const result = await createCreator.mutateAsync({
        data: {
          stageName: values.stageName,
          slug: values.slug.trim(),
        },
      });

      // Invalidate queries to refresh creators list and detail
      await queryClient.invalidateQueries({
        queryKey: getAuthControllerGetCurrentUserQueryKey(),
      });
      if (result.creatorId) {
        await queryClient.invalidateQueries({
          queryKey: getCreatorsControllerDetailCreatorQueryKey(
            result.creatorId,
          ),
        });
      }

      // Switch the app to the newly created artist
      setSelectedCreator(result);

      // Go straight to the new artist's profile editor (own profiles open in
      // edit mode) so the user can complete the optional details
      navigate({
        to: "/audiolab/creators/$CREATOR_ID",
        params: { CREATOR_ID: result.creatorId },
      });

      toast.success("Artist created successfully");
      onOpenChange(false);
    } catch {
      toast.error("Failed to create artist. Please try again.");
    }
  };

  return (
    <FormModal<AddArtistForm>
      open={open}
      onOpenChange={onOpenChange}
      title="Add Artist"
      defaultValues={{ stageName: "", slug: "" }}
      submitLabel="Add Artist"
      cancelLabel="Cancel"
      onSubmit={handleSubmit}
      size="md"
      render={(form) => {
        const { control } = form;
        return (
          <Flex direction="column" gap="5">
            <Flex direction="column" gap="2">
              <Text className={typographyTokens.label}>Artist Name</Text>
              <ControlledInputField
                name="stageName"
                control={control}
                type="text"
                placeholder="Name fans will see"
                rules={{ required: "Artist name is required" }}
              />
            </Flex>

            <Flex direction="column" gap="2">
              <Text className={typographyTokens.label}>
                Your Profile Handle
              </Text>
              <ControlledInputField
                name="slug"
                control={control}
                type="text"
                placeholder="yourname"
                prefix={SITE_URL + "/@"}
                helperText="Letters, numbers, dashes, and underscores only. 100 characters max."
                rules={{
                  required: "Profile handle is required",
                  maxLength: { value: 100, message: "100 characters max" },
                  pattern: {
                    value: /^[a-zA-Z0-9_-]+$/,
                    message: "Letters, numbers, dashes, and underscores only",
                  },
                  // Same availability probe the inline profile editor uses:
                  // resolving the slug means it's taken, 404 means free
                  validate: async (value: string) => {
                    try {
                      await creatorsControllerDetailCreator(value.trim());
                      return "This handle is already used by another artist";
                    } catch (err: unknown) {
                      const status = (err as { response?: { status?: number } })
                        ?.response?.status;
                      return status === 404
                        ? true
                        : "Couldn't verify handle availability. Please try again.";
                    }
                  },
                }}
              />
            </Flex>
          </Flex>
        );
      }}
    />
  );
}
